import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  documentation_pending: { bg:'#FFF3DC', color:'#8B5E0A' },
  compatibility_test:    { bg:'#EDF4FF', color:'#1E4FAD' },
  active:                { bg:'#E8F5EB', color:'#2E6B3A' },
  probation:             { bg:'#F3EEFF', color:'#6B3FA0' },
  confirmed:             { bg:'#E8F5EB', color:'#2E6B3A' },
  cancelled:             { bg:'#FDEAEA', color:'#8F2E24' },
};

const EMPLOYMENT_CATEGORIES = ['Full-time','Part-time','Contract','Intern','Casual'];
const PHASES = ['1','2','3','4','5','6','7','8','9','10'];
const PHASE_LABELS = {
  '1':'Phase 1 — Recruitment & Selection',
  '2':'Phase 2 — Pre-Employment Documentation',
  '3':'Phase 3 — Offer & Acceptance',
  '4':'Phase 4 — System Setup',
  '5':'Phase 5 — Orientation & Induction',
  '6':'Phase 6 — Role-Specific Training',
  '7':'Phase 7 — Compatibility Test',
  '8':'Phase 8 — Probation',
  '9':'Phase 9 — Confirmation',
  '10':'Phase 10 — First Week on the Job',
};

const ROLES = [
  { value:'sales_manager', label:'Customer Experience & Sales Manager' },
  { value:'production_team_lead', label:'Production Team Lead' },
  { value:'tailor', label:'Tailor' },
  { value:'production_assistant', label:'Production Assistant' },
  { value:'inventory_officer', label:'Inventory & Materials Officer' },
  { value:'procurement_logistics_officer', label:'Procurement & Logistics Officer' },
  { value:'front_desk_officer', label:'Front Desk Officer' },
  { value:'brand_content_officer', label:'Brand & Content Officer' },
  { value:'driver', label:'Driver' },
  { value:'support_staff', label:'Support Staff' },
  { value:'auditor', label:'Auditor' },
];

export default function Onboarding() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('checklist');
  const [form, setForm] = useState({
    employee_full_name:'', job_title:'', department:'', reports_to_name:'',
    role:'', employment_category:'Full-time', proposed_start_date:'',
    compatibility_test_start:'', compatibility_test_end:'',
    probation_start:'', probation_end:'', monthly_salary:'',
    work_location:'Ibadan, Nigeria', onboarding_coordinator:''
  });
  const [outstandingItem, setOutstandingItem] = useState('');
  const [showOutstanding, setShowOutstanding] = useState(false);
  const [jobDescriptions, setJobDescriptions] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [reviewForm, setReviewForm] = useState({ review_type:'', notes:'', outcome:'' });
  const [showReview, setShowReview] = useState(false);

  const load = () => {
    setLoading(true);
    get('/onboarding').then(d => setRecords(d.data||[])).finally(() => setLoading(false));
  };

  const loadSelected = (id) => {
    get('/onboarding/'+id).then(d => {
      const data = d.data || d;
      // Flatten checklist_by_phase into checklist_items array
      if (data.checklist_by_phase) {
        data.checklist_items = Object.values(data.checklist_by_phase).flat();
      }
      setSelected(data);
    });
  };

  useEffect(() => {
    load();
    get('/job-descriptions').then(d => setJobDescriptions((d.data||[]).filter(j=>j.status==='published'))).catch(()=>{});
    get('/users').then(d => setStaffList(d.data||[])).catch(()=>{});
  }, []);

  const createRecord = async () => {
    if (!form.employee_full_name || !form.job_title || !form.role || !form.employment_category) {
      alert('Name, job title, role and employment category are required'); return;
    }
    setSaving(true);
    try {
      const submitForm = {...form};
      delete submitForm.reports_to;
      delete submitForm.reports_to_name;
      delete submitForm.onboarding_coordinator;
      const result = await post('/onboarding', submitForm);
      setShowForm(false);
      setForm({
        employee_full_name:'', job_title:'', department:'', reports_to_name:'',
        role:'', employment_category:'Full-time', proposed_start_date:'',
        compatibility_test_start:'', compatibility_test_end:'',
        probation_start:'', probation_end:'', monthly_salary:'',
        work_location:'Ibadan, Nigeria', onboarding_coordinator:''
      });
      load();
      loadSelected(result.id);
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const completeItem = async (recordId, itemId) => {
    try {
      const token = localStorage.getItem('bb_token');
      await fetch('/api/onboarding/'+recordId+'/checklist/'+itemId+'/complete', {
        method:'PATCH',
        headers:{ Authorization:'Bearer '+token, 'Content-Type':'application/json' },
        body: JSON.stringify({ completed_by_name: user?.name })
      });
      loadSelected(recordId);
    } catch(err) { alert(err.message); }
  };

  const verifyItem = async (recordId, itemId) => {
    try {
      const token = localStorage.getItem('bb_token');
      await fetch('/api/onboarding/'+recordId+'/checklist/'+itemId+'/verify', {
        method:'PATCH',
        headers:{ Authorization:'Bearer '+token, 'Content-Type':'application/json' },
        body: JSON.stringify({ verified_by_name: user?.name })
      });
      loadSelected(recordId);
    } catch(err) { alert(err.message); }
  };

  const addOutstanding = async () => {
    if (!outstandingItem.trim()) return;
    try {
      const token = localStorage.getItem('bb_token');
      await fetch('/api/onboarding/'+selected.id+'/outstanding-items', {
        method:'POST',
        headers:{ Authorization:'Bearer '+token, 'Content-Type':'application/json' },
        body: JSON.stringify({ item_description: outstandingItem })
      });
      setOutstandingItem('');
      setShowOutstanding(false);
      loadSelected(selected.id);
    } catch(err) { alert(err.message); }
  };

  const addReview = async () => {
    if (!reviewForm.review_type || !reviewForm.outcome) {
      alert('Review type and outcome required'); return;
    }
    try {
      const token = localStorage.getItem('bb_token');
      await fetch('/api/onboarding/'+selected.id+'/reviews', {
        method:'POST',
        headers:{ Authorization:'Bearer '+token, 'Content-Type':'application/json' },
        body: JSON.stringify(reviewForm)
      });
      setShowReview(false);
      setReviewForm({ review_type:'', notes:'', outcome:'' });
      loadSelected(selected.id);
    } catch(err) { alert(err.message); }
  };

  const updateStatus = async (status) => {
    if (!window.confirm('Update status to '+status+'?')) return;
    try {
      const token = localStorage.getItem('bb_token');
      await fetch('/api/onboarding/'+selected.id, {
        method:'PATCH',
        headers:{ Authorization:'Bearer '+token, 'Content-Type':'application/json' },
        body: JSON.stringify({ status })
      });
      loadSelected(selected.id);
      load();
    } catch(err) { alert(err.message); }
  };

  // Group checklist items by phase
  const groupByPhase = (items) => {
    const grouped = {};
    for (const item of (items||[])) {
      const key = String(item.phase);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(item);
    }
    return grouped;
  };

  const getProgress = (record) => {
    if (!record.total_items || record.total_items === '0') return 0;
    return Math.round((parseInt(record.completed_items||0) / parseInt(record.total_items)) * 100);
  };

  if (!['super_admin','hr_officer'].includes(user?.role)) return (
    <div className="empty-state"><h3>Access Restricted</h3></div>
  );

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">🎯 Onboarding</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Manage new staff onboarding from documentation to confirmation
          </p>
        </div>
        <div style={{display:'flex',gap:8}}>
          {selected && (
            <button className="btn btn-ghost" onClick={()=>setSelected(null)}>
              ← All Records
            </button>
          )}
          {!selected && (
            <button className="btn btn-primary" onClick={()=>{
            setForm(f=>({...f, onboarding_coordinator: user?.name||''}));
            setShowForm(true);
          }}>
              + New Onboarding
            </button>
          )}
        </div>
      </div>

      {!selected ? (
        // Records list
        loading ? <div className="spinner"></div> : records.length === 0 ? (
          <div className="empty-state">
            <h3>No onboarding records</h3>
            <p>Click + New Onboarding to start onboarding a new staff member</p>
          </div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {records.map(r => {
              const progress = getProgress(r);
              const sc = STATUS_COLORS[r.status]||{};
              return (
                <div key={r.id} style={{background:'#fff',border:'1px solid var(--border-light)',
                  borderRadius:10,padding:16,cursor:'pointer'}}
                  onClick={()=>loadSelected(r.id)}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                    <div style={{flex:1}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                        <span style={{fontSize:14,fontWeight:600,color:'#3B2A21'}}>{r.employee_full_name}</span>
                        <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                          textTransform:'uppercase',...sc}}>{r.status?.replace(/_/g,' ')}</span>
                      </div>
                      <div style={{fontSize:11,color:'#8A8A8A'}}>
                        {r.job_title} · {r.department}
                        {r.proposed_start_date && <span> · Start: {r.proposed_start_date}</span>}
                        {r.open_outstanding_items > 0 && (
                          <span style={{color:'#8F2E24',fontWeight:600}}> · {r.open_outstanding_items} outstanding</span>
                        )}
                      </div>
                    </div>
                    <div style={{textAlign:'right',minWidth:80}}>
                      <div style={{fontSize:14,fontWeight:700,color:'#556B2F'}}>{progress}%</div>
                      <div style={{width:80,height:6,background:'#EDE7D8',borderRadius:3,marginTop:4}}>
                        <div style={{width:progress+'%',height:6,
                          background:progress===100?'#2E6B3A':'#556B2F',borderRadius:3}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        // Detail view
        <div>
          {/* Header */}
          <div style={{background:'#3B2A21',borderRadius:12,padding:'20px 24px',marginBottom:20}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <div>
                <h3 style={{color:'#A8893D',fontFamily:'Cormorant Garamond,serif',
                  fontSize:'1.5rem',margin:0}}>{selected.employee_full_name}</h3>
                <div style={{color:'rgba(242,239,231,.6)',fontSize:12,marginTop:4}}>
                  {selected.job_title} · {selected.department} · {selected.employment_category}
                </div>
                <div style={{marginTop:8,display:'flex',gap:8,flexWrap:'wrap'}}>
                  {selected.proposed_start_date && (
                    <span style={{fontSize:11,color:'rgba(242,239,231,.7)'}}>
                      Start: {selected.proposed_start_date}
                    </span>
                  )}
                  {selected.probation_end && (
                    <span style={{fontSize:11,color:'rgba(242,239,231,.7)'}}>
                      · Probation ends: {selected.probation_end}
                    </span>
                  )}
                </div>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:6,alignItems:'flex-end'}}>
                <span style={{fontSize:10,fontWeight:700,padding:'4px 12px',borderRadius:20,
                  textTransform:'uppercase',...(STATUS_COLORS[selected.status]||{})}}>
                  {selected.status?.replace(/_/g,' ')}
                </span>
                <select className="form-control" style={{fontSize:11,padding:'4px 8px',width:'auto'}}
                  value={selected.status}
                  onChange={e=>updateStatus(e.target.value)}>
                  <option value="documentation_pending">Documentation Pending</option>
                  <option value="compatibility_test">Compatibility Test</option>
                  <option value="active">Active</option>
                  <option value="probation">Probation</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{display:'flex',gap:6,marginBottom:20,
            borderBottom:'1px solid var(--border-light)',paddingBottom:12}}>
            {[['checklist','Checklist'],['outstanding','Outstanding'],
              ['reviews','Reviews'],['details','Details']
            ].map(([key,label])=>(
              <button key={key} className={'btn btn-sm '+(activeTab===key?'btn-primary':'btn-ghost')}
                onClick={()=>setActiveTab(key)}>
                {label}
                {key==='outstanding' && selected.outstanding_items?.filter(i=>i.status==='open').length > 0 && (
                  <span style={{marginLeft:6,background:'#8F2E24',color:'#fff',
                    borderRadius:10,padding:'0 6px',fontSize:10}}>
                    {selected.outstanding_items.filter(i=>i.status==='open').length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* CHECKLIST TAB */}
          {activeTab==='checklist' && (
            <div>
              {(() => {
                const grouped = groupByPhase(selected.checklist_items||[]);
                return PHASES.map(phase => {
                  const items = grouped[phase];
                  if (!items || items.length === 0) return null;
                  const done = items.filter(i=>i.status==='complete'||i.status==='verified').length;
                  return (
                    <div key={phase} style={{marginBottom:20}}>
                      <div style={{display:'flex',justifyContent:'space-between',
                        alignItems:'center',marginBottom:10}}>
                        <div style={{fontSize:12,fontWeight:700,color:'#556B2F',
                          textTransform:'uppercase',letterSpacing:'.08em'}}>
                          {PHASE_LABELS[phase]||phase}
                        </div>
                        <span style={{fontSize:11,color:'#8A8A8A'}}>{done}/{items.length}</span>
                      </div>
                      {items.map(item => (
                        <div key={item.id} style={{background:'#fff',
                          border:'1px solid '+(item.status==='verified'?'#556B2F':
                            item.status==='complete'?'#B8DEC0':'var(--border-light)'),
                          borderRadius:8,padding:'12px 14px',marginBottom:8,
                          display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,color:'#3B2A21',
                              textDecoration:item.status==='verified'?'line-through':'none',
                              fontWeight:item.status==='pending'?400:500}}>
                              {item.item_text}
                            </div>
                            <div style={{fontSize:10,color:'#8A8A8A',marginTop:2}}>
                              {item.category}
                              {item.completed_by_name && <span> · Done by {item.completed_by_name}</span>}
                              {item.verified_by_name && <span> · Verified by {item.verified_by_name}</span>}
                            </div>
                          </div>
                          <div style={{display:'flex',gap:6,marginLeft:12}}>
                            {item.status==='pending' && (
                              <button className="btn btn-outline btn-sm"
                                style={{fontSize:10,color:'#2E6B3A',borderColor:'#2E6B3A'}}
                                onClick={()=>completeItem(selected.id, item.id)}>
                                ✓ Done
                              </button>
                            )}
                            {item.status==='complete' && (
                              <button className="btn btn-outline btn-sm"
                                style={{fontSize:10,color:'#556B2F',borderColor:'#556B2F'}}
                                onClick={()=>verifyItem(selected.id, item.id)}>
                                ✓ Verify
                              </button>
                            )}
                            {item.status==='verified' && (
                              <span style={{fontSize:10,color:'#556B2F',fontWeight:600}}>✓ Verified</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                });
              })()}
              {(!selected.checklist_items || selected.checklist_items.length === 0) && (
                <div className="empty-state">
                  <h3>No checklist items</h3>
                  <p>Add checklist templates in settings to auto-populate items</p>
                </div>
              )}
            </div>
          )}

          {/* OUTSTANDING TAB */}
          {activeTab==='outstanding' && (
            <div>
              <div style={{display:'flex',justifyContent:'space-between',
                alignItems:'center',marginBottom:16}}>
                <div style={{fontSize:13,color:'#8A8A8A'}}>
                  {(selected.outstanding_items||[]).filter(i=>i.status==='open').length} open items
                </div>
                <button className="btn btn-outline btn-sm"
                  onClick={()=>setShowOutstanding(true)}>
                  + Add Outstanding Item
                </button>
              </div>

              {showOutstanding && (
                <div style={{background:'#FFF3DC',border:'1px solid #F5C842',
                  borderRadius:8,padding:12,marginBottom:16}}>
                  <div className="form-group">
                    <label className="form-label">Outstanding Item *</label>
                    <input className="form-control" value={outstandingItem}
                      onChange={e=>setOutstandingItem(e.target.value)}
                      placeholder="e.g. Submit guarantor form, Provide NYSC certificate..." />
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-outline btn-sm"
                      onClick={()=>setShowOutstanding(false)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={addOutstanding}>
                      Add Item
                    </button>
                  </div>
                </div>
              )}

              {(selected.outstanding_items||[]).length === 0 ? (
                <div className="empty-state"><h3>No outstanding items</h3></div>
              ) : (selected.outstanding_items||[]).map(item => (
                <div key={item.id} style={{background:'#fff',
                  border:'1px solid '+(item.status==='open'?'#F5B8B8':'var(--border-light)'),
                  borderRadius:8,padding:'12px 14px',marginBottom:8,
                  display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <div style={{fontSize:13,color:'#3B2A21',
                      textDecoration:item.status==='resolved'?'line-through':'none'}}>
                      {item.item_description}
                    </div>
                    {item.resolved_at && (
                      <div style={{fontSize:10,color:'#8A8A8A',marginTop:2}}>
                        Resolved: {new Date(item.resolved_at).toLocaleDateString('en-NG')}
                      </div>
                    )}
                  </div>
                  <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                    textTransform:'uppercase',
                    background:item.status==='open'?'#FDEAEA':'#E8F5EB',
                    color:item.status==='open'?'#8F2E24':'#2E6B3A'}}>
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* REVIEWS TAB */}
          {activeTab==='reviews' && (
            <div>
              <div style={{display:'flex',justifyContent:'space-between',
                alignItems:'center',marginBottom:16}}>
                <div style={{fontSize:13,color:'#8A8A8A'}}>
                  {(selected.reviews||[]).length} review{(selected.reviews||[]).length!==1?'s':''}
                </div>
                <button className="btn btn-outline btn-sm"
                  onClick={()=>setShowReview(true)}>
                  + Add Review
                </button>
              </div>

              {showReview && (
                <div style={{background:'#F2EFE7',border:'1px solid #A8893D',
                  borderRadius:8,padding:16,marginBottom:16}}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Review Type *</label>
                      <select className="form-control" value={reviewForm.review_type}
                        onChange={e=>setReviewForm({...reviewForm,review_type:e.target.value})}>
                        <option value="">Select...</option>
                        <option value="week_1">Week 1 Review</option>
                        <option value="week_2">Week 2 Review</option>
                        <option value="month_1">Month 1 Review</option>
                        <option value="month_3">Month 3 Review</option>
                        <option value="compatibility_test">Compatibility Test</option>
                        <option value="probation_midpoint">Probation Midpoint</option>
                        <option value="probation_end">Probation End</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Outcome *</label>
                      <select className="form-control" value={reviewForm.outcome}
                        onChange={e=>setReviewForm({...reviewForm,outcome:e.target.value})}>
                        <option value="">Select...</option>
                        <option value="pass">Pass</option>
                        <option value="pass_with_conditions">Pass with Conditions</option>
                        <option value="extend">Extend Period</option>
                        <option value="fail">Fail</option>
                        <option value="terminate">Terminate</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <textarea className="form-control" rows={3} value={reviewForm.notes}
                      onChange={e=>setReviewForm({...reviewForm,notes:e.target.value})}
                      placeholder="Review observations, feedback, decisions..." />
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-outline btn-sm"
                      onClick={()=>setShowReview(false)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={addReview}>
                      Save Review
                    </button>
                  </div>
                </div>
              )}

              {(selected.reviews||[]).length === 0 ? (
                <div className="empty-state"><h3>No reviews yet</h3></div>
              ) : (selected.reviews||[]).map(r => (
                <div key={r.id} style={{background:'#fff',
                  border:'1px solid var(--border-light)',borderRadius:8,
                  padding:'14px 16px',marginBottom:8}}>
                  <div style={{display:'flex',justifyContent:'space-between',
                    alignItems:'flex-start',marginBottom:6}}>
                    <div style={{fontSize:13,fontWeight:600,color:'#3B2A21'}}>
                      {r.review_type?.replace(/_/g,' ')}
                    </div>
                    <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                      textTransform:'uppercase',
                      background:r.outcome==='pass'||r.outcome==='pass_with_conditions'?'#E8F5EB':
                        r.outcome==='fail'||r.outcome==='terminate'?'#FDEAEA':'#FFF3DC',
                      color:r.outcome==='pass'||r.outcome==='pass_with_conditions'?'#2E6B3A':
                        r.outcome==='fail'||r.outcome==='terminate'?'#8F2E24':'#8B5E0A'}}>
                      {r.outcome?.replace(/_/g,' ')}
                    </span>
                  </div>
                  {r.notes && <div style={{fontSize:12,color:'#555'}}>{r.notes}</div>}
                  <div style={{fontSize:10,color:'#8A8A8A',marginTop:6}}>
                    {new Date(r.created_at).toLocaleDateString('en-NG')}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* DETAILS TAB */}
          {activeTab==='details' && (
            <div style={{background:'#fff',border:'1px solid var(--border-light)',
              borderRadius:10,padding:20}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                {[
                  ['Full Name', selected.employee_full_name],
                  ['Job Title', selected.job_title],
                  ['Department', selected.department],
                  ['Reports To', selected.reports_to],
                  ['Role', ROLES.find(r=>r.value===selected.role)?.label||selected.role],
                  ['Employment Category', selected.employment_category],
                  ['Work Location', selected.work_location],
                  ['Monthly Salary', selected.monthly_salary ? '₦'+Number(selected.monthly_salary).toLocaleString() : '—'],
                  ['Proposed Start', selected.proposed_start_date||'—'],
                  ['Compatibility Test', selected.compatibility_test_start ? selected.compatibility_test_start+' to '+selected.compatibility_test_end : '—'],
                  ['Probation Period', selected.probation_start ? selected.probation_start+' to '+selected.probation_end : '—'],
                  ['Onboarding Coordinator', selected.onboarding_coordinator||'—'],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div style={{fontSize:9,fontWeight:600,color:'#8A8A8A',
                      textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>
                      {label}
                    </div>
                    <div style={{fontSize:13,color:'#3B2A21'}}>{value||'—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* New Onboarding Form */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:600}}>
            <div className="modal-hdr">
              <span className="modal-title">New Onboarding Record</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'75vh',overflowY:'auto'}}>
              <div className="form-row">
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Full Name *</label>
                  <input className="form-control" value={form.employee_full_name}
                    onChange={e=>setForm({...form,employee_full_name:e.target.value})}
                    placeholder="Employee full name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Employment Category *</label>
                  <select className="form-control" value={form.employment_category}
                    onChange={e=>setForm({...form,employment_category:e.target.value})}>
                    {EMPLOYMENT_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Job Title *</label>
                  <select className="form-control" value={form.job_title}
                    onChange={e=>{
                      const jd = jobDescriptions.find(j=>j.job_title===e.target.value);
                      setForm({...form,
                        job_title:e.target.value,
                        department:jd?.department||form.department
                      });
                    }}>
                    <option value="">Select job title...</option>
                    {jobDescriptions.map(j=>(
                      <option key={j.id} value={j.job_title}>{j.job_title}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <select className="form-control" value={form.role}
                    onChange={e=>setForm({...form,role:e.target.value})}>
                    <option value="">Select role...</option>
                    {ROLES.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <input className="form-control" value={form.department}
                    onChange={e=>setForm({...form,department:e.target.value})}
                    placeholder="e.g. Production" />
                </div>
                <div className="form-group">
                  <label className="form-label">Reports To</label>
                  <select className="form-control" value={form.reports_to_name||''}
                    onChange={e=>setForm({...form,reports_to_name:e.target.value})}>
                    <option value="">Select...</option>
                    {staffList.filter(s=>['super_admin','admin','hr_officer','production_team_lead','sales_manager'].includes(s.role)).map(s=>(
                      <option key={s.id} value={s.name}>{s.name} — {s.role.replace(/_/g,' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Proposed Start Date</label>
                  <input type="date" className="form-control" value={form.proposed_start_date}
                    onChange={e=>setForm({...form,proposed_start_date:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Monthly Salary (₦)</label>
                  <input type="number" className="form-control" value={form.monthly_salary}
                    onChange={e=>setForm({...form,monthly_salary:e.target.value})}
                    placeholder="0" />
                </div>
              </div>
              <div style={{fontSize:11,fontWeight:600,color:'#556B2F',marginBottom:8,marginTop:8}}>
                Compatibility Test Period
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input type="date" className="form-control" value={form.compatibility_test_start}
                    onChange={e=>setForm({...form,compatibility_test_start:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input type="date" className="form-control" value={form.compatibility_test_end}
                    onChange={e=>setForm({...form,compatibility_test_end:e.target.value})} />
                </div>
              </div>
              <div style={{fontSize:11,fontWeight:600,color:'#556B2F',marginBottom:8}}>
                Probation Period
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input type="date" className="form-control" value={form.probation_start}
                    onChange={e=>setForm({...form,probation_start:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input type="date" className="form-control" value={form.probation_end}
                    onChange={e=>setForm({...form,probation_end:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Work Location</label>
                  <input className="form-control" value={form.work_location}
                    onChange={e=>setForm({...form,work_location:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Onboarding Coordinator</label>
                  <input className="form-control" value={form.onboarding_coordinator||user?.name||''}
                    readOnly style={{background:'#F9F8F5',color:'#8A8A8A'}} />
                </div>
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={createRecord} disabled={saving}>
                {saving?'Creating...':'Create Onboarding Record'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
